import { useEffect, useImperativeHandle, useRef, useState, forwardRef } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Eraser, Loader2, PenLine, X } from 'lucide-react';
import { instructorApi } from '../api/instructor';
import { InstructorLesson } from '../types';

export interface SignaturePadHandle {
  isEmpty: () => boolean;
  toDataURL: () => string;
}

/** Zone de signature tactile réutilisable (doigt / souris). */
const SignaturePad = forwardRef<SignaturePadHandle, { label: string }>(
  function SignaturePad({ label }, ref) {
    const canvasRef = useRef<HTMLCanvasElement | null>(null);
    const drawing = useRef(false);
    const dirty = useRef(false);
    const [, force] = useState(0);

    useEffect(() => {
      const canvas = canvasRef.current;
      if (!canvas) return;
      const ratio = window.devicePixelRatio || 1;
      const rect = canvas.getBoundingClientRect();
      canvas.width = rect.width * ratio;
      canvas.height = rect.height * ratio;
      const ctx = canvas.getContext('2d');
      if (ctx) {
        ctx.scale(ratio, ratio);
        ctx.lineWidth = 2.5;
        ctx.lineCap = 'round';
        ctx.lineJoin = 'round';
        ctx.strokeStyle = '#0f172a';
      }
    }, []);

    useImperativeHandle(ref, () => ({
      isEmpty: () => !dirty.current,
      toDataURL: () => canvasRef.current?.toDataURL('image/png') ?? '',
    }));

    const point = (e: React.PointerEvent<HTMLCanvasElement>) => {
      const rect = e.currentTarget.getBoundingClientRect();
      return { x: e.clientX - rect.left, y: e.clientY - rect.top };
    };
    const start = (e: React.PointerEvent<HTMLCanvasElement>) => {
      e.preventDefault();
      const ctx = canvasRef.current?.getContext('2d');
      if (!ctx) return;
      drawing.current = true;
      const { x, y } = point(e);
      ctx.beginPath();
      ctx.moveTo(x, y);
      e.currentTarget.setPointerCapture(e.pointerId);
    };
    const move = (e: React.PointerEvent<HTMLCanvasElement>) => {
      if (!drawing.current) return;
      e.preventDefault();
      const ctx = canvasRef.current?.getContext('2d');
      if (!ctx) return;
      const { x, y } = point(e);
      ctx.lineTo(x, y);
      ctx.stroke();
      if (!dirty.current) {
        dirty.current = true;
        force((n) => n + 1);
      }
    };
    const end = () => {
      drawing.current = false;
    };
    const clear = () => {
      const canvas = canvasRef.current;
      const ctx = canvas?.getContext('2d');
      if (canvas && ctx) ctx.clearRect(0, 0, canvas.width, canvas.height);
      dirty.current = false;
      force((n) => n + 1);
    };

    return (
      <div>
        <div className="flex items-center justify-between mb-1">
          <span className="text-xs font-medium text-slate-600">{label}</span>
          <button
            type="button"
            onClick={clear}
            disabled={!dirty.current}
            className="inline-flex items-center gap-1 text-xs text-slate-500 hover:text-slate-800 disabled:opacity-40"
          >
            <Eraser size={13} />
            Effacer
          </button>
        </div>
        <div className="rounded-xl border-2 border-dashed border-slate-300 bg-slate-50">
          <canvas
            ref={canvasRef}
            onPointerDown={start}
            onPointerMove={move}
            onPointerUp={end}
            onPointerLeave={end}
            className="w-full h-36 touch-none rounded-xl cursor-crosshair"
          />
        </div>
      </div>
    );
  },
);

/**
 * Émargement : l'élève signe sa présence, le moniteur peut contre-signer pour
 * valider la séance. Les signatures (PNG) sont envoyées au backend.
 */
export default function AttendanceModal({
  lesson,
  onClose,
}: {
  lesson: InstructorLesson;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const studentPad = useRef<SignaturePadHandle>(null);
  const instructorPad = useRef<SignaturePadHandle>(null);
  const [error, setError] = useState<string | null>(null);
  const candidate = lesson.candidate;
  const candidateName = candidate
    ? `${candidate.firstName} ${candidate.lastName}`
    : 'Élève';

  const mutation = useMutation({
    mutationFn: () => {
      const signature = studentPad.current?.toDataURL() ?? '';
      const instructorSignature =
        instructorPad.current && !instructorPad.current.isEmpty()
          ? instructorPad.current.toDataURL()
          : undefined;
      return instructorApi.signAttendance(lesson.id, signature, instructorSignature);
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['today'] });
      void queryClient.invalidateQueries({ queryKey: ['planning'] });
      void queryClient.invalidateQueries({ queryKey: ['students'] });
      onClose();
    },
    onError: (e: Error) => setError(e.message || 'Émargement impossible.'),
  });

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 px-4">
      <div className="w-full max-w-lg bg-white rounded-2xl shadow-2xl border border-slate-200 max-h-[92vh] overflow-y-auto animate-fade-in">
        <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between">
          <div>
            <h2 className="text-slate-900 text-lg font-semibold flex items-center gap-2">
              <PenLine size={18} className="text-indigo-600" />
              Émargement
            </h2>
            <p className="text-xs text-slate-500">
              {candidateName} atteste sa présence à la leçon
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-lg hover:bg-slate-100 transition-colors"
            aria-label="Fermer"
          >
            <X size={18} className="text-slate-500" />
          </button>
        </div>

        <div className="px-6 py-5 space-y-4">
          <SignaturePad ref={studentPad} label={`Signature de l'élève (${candidateName})`} />
          <SignaturePad
            ref={instructorPad}
            label="Contre-signature du moniteur (optionnel)"
          />

          {error && <p className="text-sm text-red-600">{error}</p>}

          <div className="flex items-center justify-end gap-2 pt-1">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2.5 rounded-lg text-sm font-medium text-slate-600 hover:bg-slate-100"
            >
              Annuler
            </button>
            <button
              type="button"
              onClick={() => {
                if (studentPad.current?.isEmpty()) {
                  setError("La signature de l'élève est requise.");
                  return;
                }
                setError(null);
                mutation.mutate();
              }}
              disabled={mutation.isPending}
              className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-semibold shadow-sm disabled:opacity-50"
            >
              {mutation.isPending && <Loader2 size={15} className="animate-spin" />}
              Valider l'émargement
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
